import { useState, FormEvent, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Spinner } from "../ui/Spinner";
import { api, mensajeError } from "../../api/client";

export const MOTIVOS_BAJA_CLIENTE = [
  { value: "YA_NO_ES_CLIENTE", label: "Ya no es cliente del negocio" },
  { value: "PETICION_CLIENTE", label: "Petición del cliente" },
  { value: "FALLECIMIENTO", label: "Falleció" },
  { value: "DUPLICADO", label: "Registro duplicado" },
  { value: "OTRO", label: "Otro motivo" },
];

interface ClienteBaja {
  id: string;
  nombre: string;
}

interface Props {
  cliente: ClienteBaja | null;
  onClose: () => void;
  onBajaExitosa: () => void;
}

// Modal para "dar de baja" (desvincular) un cliente del negocio: pide un
// motivo obligatorio antes de confirmar. No borra nada — un borrado físico
// real fallaría de todos modos en cuanto el cliente tuviera cualquier
// mascota, cita, venta o factura registrada (por eso existía el error
// "error interno del servidor" al intentar eliminar un cliente con
// historial). Mismo patrón que ModalBajaMascota.
export function ModalBajaCliente({ cliente, onClose, onBajaExitosa }: Props) {
  const [motivo, setMotivo] = useState("YA_NO_ES_CLIENTE");
  const [detalle, setDetalle] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cliente) {
      setMotivo("YA_NO_ES_CLIENTE");
      setDetalle("");
      setError(null);
    }
  }, [cliente]);

  async function confirmar(e: FormEvent) {
    e.preventDefault();
    if (!cliente) return;
    setGuardando(true);
    setError(null);
    try {
      await api.post(`/clientes/${cliente.id}/eliminar-del-negocio`, {
        motivo,
        motivoDetalle: motivo === "OTRO" ? detalle.trim() : undefined,
      });
      onBajaExitosa();
    } catch (err) {
      setError(mensajeError(err));
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open={!!cliente} onClose={onClose} title={`Dar de baja a ${cliente?.nombre ?? ""}`}>
      <form onSubmit={confirmar} className="space-y-4">
        <p className="rounded-xl bg-slate-50 p-3 text-sm text-slate-500">
          Este cliente dejará de aparecer en tu panel, pero su información <strong>no se borra</strong>: sus mascotas,
          citas, ventas y facturas anteriores se conservan intactas, y ya no se le podrán registrar mascotas nuevas.
        </p>
        <div>
          <label className="label">Motivo de la baja</label>
          <select className="input" value={motivo} onChange={(e) => setMotivo(e.target.value)} required>
            {MOTIVOS_BAJA_CLIENTE.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
        </div>
        {motivo === "OTRO" && (
          <div>
            <label className="label">Cuéntanos brevemente</label>
            <textarea
              className="input"
              rows={2}
              required
              value={detalle}
              onChange={(e) => setDetalle(e.target.value)}
              placeholder="Ej. registro de prueba"
            />
          </div>
        )}
        {error && <p className="text-sm text-red-600">{error}</p>}
        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
            disabled={guardando}
          >
            {guardando && <Spinner className="h-4 w-4 text-white" />}
            Confirmar baja
          </button>
        </div>
      </form>
    </Modal>
  );
}
